import { useState, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Helmet } from 'react-helmet-async'
import { API_BASE_URL } from 'config/api'
import { getValidAccessToken } from 'utils/tokenManager'

// const apiUrl = 'https://api.gobookstory.com'

interface IFormInput {
  currentPassword: number | string
  newPassword: number | string
  newPasswordConfirmation: number | string
}

export const ChangePasswordForm = () => {
  const [show, setShow] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<IFormInput>()
  const onSubmit: SubmitHandler<IFormInput> = async (options) => {
    try {
      const userToken = await getValidAccessToken()
      let data = {
        currentPassword: options.currentPassword,
        newPassword: options.newPassword,
        newPasswordConfirmation: options.newPasswordConfirmation,
      }
      const config = {
        method: 'PUT',
        data: data,
        url: `${API_BASE_URL}/user/password`,
        headers: {
          Authorization: 'bearer ' + userToken,
        },
      }
      await axios(config).then((res) => {
        if (res.status === 200) setShow(true)
      })
    } catch (error) {
      console.error('Failed to change password:', error)
      // Handle error appropriately
    }
  }

  useEffect(() => {
    setFocus('currentPassword')
  }, [setFocus])

  const handleShowCurrentPasswordClick = (e: any) => {
    e.preventDefault()
    setShowCurrentPassword(!showCurrentPassword)
  }

  const handleShowNewPasswordClick = (e: any) => {
    e.preventDefault()
    setShowNewPassword(!showNewPassword)
  }

  const handleShowConfirmPasswordClick = (e: any) => {
    e.preventDefault()
    setShowConfirmPassword(!showConfirmPassword)
  }

  const handleBackToSettingsClick = () => {
    navigate('/settings')
  }

  const handleGoToMainPageClick = () => {
    navigate('/')
  }

  return (
    <div className='max-w-lg mt-5 mx-auto flex flex-col my-5 h-auto border py-8 px-4 rounded-md' style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          تغییر رمز کاربری
        </title>
        <meta
          name='description'
          content='داستان کتاب - داستان ها همیشه در دل و ذهن می مانند'
        />
      </Helmet>
      <div className={show ? 'block' : 'hidden'}>
        <h2 className='text-lg text-start font-medium '>
          رمز عبور شما با موفقیت تغییر کرد.
        </h2>
        <div className='flex items-center justify-between w-full gap-4 my-4'>
          <button
            onClick={handleBackToSettingsClick}
            className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            رفتن به تنظمیات
          </button>
          <button
            onClick={handleGoToMainPageClick}
            className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            رفتن به صفحه اصلی
          </button>
        </div>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={!show ? 'block' : 'hidden'}
      >
        <button
          className='rounded border-none w-full mb-4 p-4 text-white cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          onClick={handleBackToSettingsClick}
        >
          بازگشت به تنظیمات
        </button>
        <h1 className='text-lg text-start font-medium mt-4 mb-2'>
          لطفا رمز فعلی خود را وارد کنید:
        </h1>
        <div className='relative'>
          <input
            autoFocus
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type={showCurrentPassword ? 'text' : 'password'}
            {...register('currentPassword', {
              required: 'current password is required',
              minLength: { value: 8, message: 'Min length is 8' },
            })}
            dir='ltr'
          />

          <button
            type='button'
            className='absolute top-4 right-4'
            onClick={handleShowCurrentPasswordClick}
          >
            {showCurrentPassword ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{ fontSize: 16, marginLeft: 12, color: 'var(--text-primary)' }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faEye}
                style={{ fontSize: 16, marginLeft: 12, color: 'var(--text-primary)' }}
              />
            )}
          </button>
          {errors.currentPassword && (
            <p className='text-red-600 before:inline before:content-["⚠"]'>
              {' '}
              {errors.currentPassword.message}
            </p>
          )}
        </div>
        <h1 className='text-lg text-start font-medium mt-4 mb-2'>
          لطفا رمز جدید خود را وارد کنید:
        </h1>
        <div className='relative'>
          <input
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type={showNewPassword ? 'text' : 'password'}
            {...register('newPassword', {
              required: 'new password is required',
              minLength: { value: 8, message: 'Min length is 8' },
            })}
            dir='ltr'
          />

          <button
            type='button'
            className='absolute top-4 right-3'
            onClick={handleShowNewPasswordClick}
          >
            {showNewPassword ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{ fontSize: 16, marginLeft: 12, color: 'black' }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faEye}
                style={{ fontSize: 16, marginLeft: 12, color: 'black' }}
              />
            )}
          </button>
          {errors.newPassword && (
            <p className='text-red-600 before:inline before:content-["⚠"]'>
              {' '}
              {errors.newPassword.message}
            </p>
          )}
        </div>
        <h1 className='text-lg text-start font-medium mt-4 mb-2'>
          لطفا رمز جدید خود را دوباره وارد کنید:
        </h1>
        <div className='relative'>
          <input
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type={showConfirmPassword ? 'text' : 'password'}
            {...register('newPasswordConfirmation', {
              required: 'new password confirmation is required',
              minLength: { value: 8, message: 'Min length is 8' },
            })}
            dir='ltr'
          />

          <button
            type='button'
            className='absolute top-4 right-3'
            onClick={handleShowConfirmPasswordClick}
          >
            {showConfirmPassword ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{ fontSize: 16, marginLeft: 12, color: 'black' }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faEye}
                style={{ fontSize: 16, marginLeft: 12, color: 'black' }}
              />
            )}
          </button>
          {errors.newPasswordConfirmation && (
            <p className='text-red-600 before:inline before:content-["⚠"]'>
              {' '}
              {errors.newPasswordConfirmation.message}
            </p>
          )}
        </div>
        <input
          className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value='تایید'
        />
      </form>
    </div>
  )
}
